import { CalendarMonth } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, CircularProgress } from "@mui/material";
import { endOfWeek, startOfWeek } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { colors } from "@components/ui/colors";
import { useDispatch } from "react-redux";
import { useModals } from "../../context/ModalContext";
import { resetJobCreateStepper } from "../../redux/features/jobCreateSlice";
import { selectActiveCompanyId, selectCurrentCompany, selectRole } from "../../redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "../../redux/hooks/redux";
import { IRoles, widgetSmallHeight } from "../../utils";
import { WidgetComponentCharts } from "../common/WidgetComponentCharts";
import { WidgetMenuActions } from "../common/WidgetMenuActions/WidgetMenuActions";
import { JobsList } from "../jobs/jobs-list/JobsList";
import { SASMenuStyles } from "../ui/styles/inputStyles";
import { CalendarJobsCustom } from "./CalendarJobsCustom";
import Dayjobs from "./Dayjobs";
import HeaderJobsCalendar from "./HeaderJobsCalendar";
import JobCalendarSeccion from "./JobCalendarSeccion";
import { JobsCreate } from "./JobCreate/Jobs.layout";
import { useJobCreateWindowStore } from "./JobCreate/store/useJobCreateWindowStore";
import JobsWidgetWithWeek from "./JobsWidgetWithWeek";
import { useJobEventsData } from "./useJobDatas";
import { getCompanyDateRange, getCompanyMonthRange, getCompanyToday, jobOverlapsDay } from "./utils/jobOverlapsRange";
import { useCompanySelectedDate } from "./utils/useCompanySelectedDate";

export const JobsCalendarWidget = ({ swapyName, ...props }) => {
  const navigate = useNavigate();
  const role = useAppSelector(selectRole);
  const activeCompanyId = useAppSelector(selectActiveCompanyId);
  const companyTimeZone = useAppSelector(selectCurrentCompany)?.timeZone || "UTC";
  const dispatch = useDispatch();
  const { openModal } = useModals();
  const scrollContainerRef = useRef(null);
  const restoredJobCreateWindowRef = useRef(false);
  const openJobCreateWindow = useJobCreateWindowStore((store) => store.openWindow);
  const clearJobCreateWindow = useJobCreateWindowStore((store) => store.clearWindow);
  const isJobCreateWindowOpen = useJobCreateWindowStore((store) => store.isOpen);
  const savedJobCreateModalKey = useJobCreateWindowStore((store) => store.modalKey);
  const savedJobCreateCompanyId = useJobCreateWindowStore((store) => store.companyId);
  const hasJobCreateWindowHydrated = useJobCreateWindowStore((store) => store.hasHydrated);

  const [jobFilter, setJobFilter] = useState("all");
  const [viewPeriod, setViewPeriod] = useState("today");
  const [open, setOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [selectedDate, setSelectedDate] = useCompanySelectedDate(activeCompanyId, companyTimeZone);

  const today = useMemo(() => getCompanyToday(companyTimeZone), [companyTimeZone]);

  const fixedWeekRange = useMemo(() => {
    return {
      start: startOfWeek(today, { weekStartsOn: 0 }),
      end: endOfWeek(today, { weekStartsOn: 0 }),
    };
  }, [today]);

  const safeSelectedDate = useMemo(() => {
    if (selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())) {
      return selectedDate;
    }

    return getCompanyToday(companyTimeZone);
  }, [selectedDate, companyTimeZone]);

  const queryRange = useMemo(() => {
    if (viewPeriod === "monthly") {
      return getCompanyMonthRange(safeSelectedDate, companyTimeZone);
    }

    if (viewPeriod === "weekly") {
      return getCompanyDateRange(fixedWeekRange.start, fixedWeekRange.end, companyTimeZone);
    }

    return getCompanyDateRange(safeSelectedDate, safeSelectedDate, companyTimeZone);
  }, [viewPeriod, safeSelectedDate, fixedWeekRange, companyTimeZone]);

  const { jobsReal, isLoading } = useJobEventsData(queryRange.start, queryRange.end);

  const jobsForCalendar = useMemo(() => {
    const currentFilter = jobFilter.toLowerCase();
    const list = jobsReal ?? [];

    return currentFilter === "all" ? [...list] : list.filter((job) => job?.type?.toLowerCase() === currentFilter);
  }, [jobFilter, jobsReal]);

  const jobsForList = useMemo(() => {
    if (!selectedDate) return [];

    let list = jobsForCalendar.filter((job) => jobOverlapsDay(job, selectedDate, companyTimeZone));

    if (selectedJobId) {
      const selectedItem = list.find((job) => String(job.id) === String(selectedJobId));

      if (selectedItem) {
        const others = list.filter((job) => String(job.id) !== String(selectedJobId));
        return [selectedItem, ...others];
      }
    }

    return list;
  }, [jobsForCalendar, selectedDate, selectedJobId, companyTimeZone]);

  const selectedJob = useMemo(() => {
    return jobsForCalendar.find((item) => String(item?.id) === String(selectedJobId));
  }, [selectedJobId, jobsForCalendar]);

  const onCloseModalRestart = useCallback(() => {
    clearJobCreateWindow();
    dispatch(resetJobCreateStepper());
  }, [clearJobCreateWindow, dispatch]);

  const buildJobCreateModalContent = useCallback((modalKey) => {
    return <JobsCreate withQuoteService={!modalKey.includes("Without-Quote")} />;
  }, []);

  useEffect(() => {
    if (restoredJobCreateWindowRef.current || !hasJobCreateWindowHydrated || !isJobCreateWindowOpen || !savedJobCreateModalKey) return;
    if (!savedJobCreateCompanyId || !activeCompanyId || String(savedJobCreateCompanyId) !== String(activeCompanyId)) return;

    restoredJobCreateWindowRef.current = true;
    openModal(savedJobCreateModalKey, buildJobCreateModalContent(savedJobCreateModalKey), onCloseModalRestart);
  }, [
    buildJobCreateModalContent,
    hasJobCreateWindowHydrated,
    isJobCreateWindowOpen,
    onCloseModalRestart,
    openModal,
    activeCompanyId,
    savedJobCreateCompanyId,
    savedJobCreateModalKey,
  ]);

  const JOBS_ACTIONS = useMemo(
    () => [
      ...(role !== IRoles.Employee
        ? [
            {
              label: "Create Job",
              icon: <AddIcon />,
              subActions: [
                {
                  label: "From Scratch",
                  icon: <AddIcon />,
                  modalComponent: <JobsCreate />,
                  modalKey: "Dashboard-JobsAdd-Business-Without-Quote",
                  onClick: () => openJobCreateWindow("Dashboard-JobsAdd-Business-Without-Quote", activeCompanyId),
                  onConfirmClose: onCloseModalRestart,
                },
                {
                  label: "From a Quote Service",
                  icon: <AddIcon />,
                  modalComponent: <JobsCreate withQuoteService />,
                  modalKey: "Dashboard-JobsAdd-Business",
                  onClick: () => openJobCreateWindow("Dashboard-JobsAdd-Business", activeCompanyId),
                  onConfirmClose: onCloseModalRestart,
                },
              ],
            },
          ]
        : []),
      {
        label: "See Jobs",
        icon: <VisibilityIcon />,
        modalComponent: <JobsList />,
        modalKey: "Dashboard-Jobs-Business",
      },
      {
        label: "Advanced Calendar",
        icon: <CalendarMonth />,
        modalComponent: <CalendarJobsCustom />,
        modalKey: "Dashboard-Jobs-Calendar",
      },
      ...(role !== IRoles.Employee
        ? [
            {
              label: "Agenda Inteligente",
              icon: <AutoAwesomeIcon />,
              onClick: () => navigate(role === IRoles.SuperAdmin ? "/admin/agenda-inteligente" : "/business/agenda-inteligente"),
            },
          ]
        : []),
    ],
    [activeCompanyId, openJobCreateWindow, role, onCloseModalRestart, navigate],
  );

  const handleOpen = (jobId) => {
    if (selectedJobId === jobId && open) {
      handleCloseDetail();
      return;
    }

    setSelectedJobId(jobId);
    setOpen(true);
  };

  const handleCloseDetail = () => {
    setOpen(false);
    setSelectedJobId("");
  };

  const calendarSectionProps = {
    open,
    selectedJobId,
    scrollContainerRef,
    jobsForList,
    handleOpen,
    selectedJob,
    jobsForCalendar,
    selectedDate,
    setSelectedDate,
    handleCloseDetail,
    timeZone: companyTimeZone,
  };

  return (
    <Box
      data-swapy-slot={swapyName || "jobs-calendar"}
      sx={{
        height: widgetSmallHeight,
        minHeight: widgetSmallHeight,
        position: "relative",
      }}
    >
      <Box
        data-swapy-item={swapyName || "jobs-calendar"}
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          data-swapy-handle
          sx={{
            height: "40px",
            width: "100%",
            cursor: "grab",
            position: "absolute",
            zIndex: 2,
          }}
        />

        <WidgetComponentCharts
          title="Jobs & Calendar"
          icon={<CalendarMonthIcon />}
          sxProp={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
          data={
            isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress size={80} sx={{ color: colors.orange.base }} />
              </Box>
            ) : viewPeriod === "weekly" ? (
              <JobsWidgetWithWeek
                filter={jobFilter}
                period={viewPeriod}
                jobs={jobsForCalendar}
                isLoading={isLoading}
                selectedDate={today}
                lockWeek
                timeZone={companyTimeZone}
              />
            ) : viewPeriod === "today" ? (
              <Dayjobs
                filter={jobFilter}
                period={viewPeriod}
                jobs={jobsForCalendar}
                isLoading={isLoading}
                selectedDate={safeSelectedDate}
                timeZone={companyTimeZone}
              />
            ) : (
              <JobCalendarSeccion {...calendarSectionProps} />
            )
          }
          actions={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HeaderJobsCalendar
                job={jobFilter}
                setJob={setJobFilter}
                period={viewPeriod}
                setPeriod={(period) => {
                  setViewPeriod(period);
                  setSelectedJobId("");
                  setOpen(false);

                  if (period === "today") {
                    setSelectedDate(getCompanyToday(companyTimeZone));
                  }
                }}
              />

              <WidgetMenuActions actionList={JOBS_ACTIONS} sx={SASMenuStyles} />
            </Box>
          }
        />
      </Box>
    </Box>
  );
};
